
import React from 'react';
import { MapPin, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Itinerary {
  id: string;
  title: string;
  destination?: string | null;
  slug?: string | null;
}

interface BlogItinerariesCardProps {
  itineraries: Itinerary[];
  isNavigating: boolean;
}

export function BlogItinerariesCard({ itineraries, isNavigating }: BlogItinerariesCardProps) {
  const navigate = useNavigate();
  
  if (!itineraries || itineraries.length === 0) {
    return null;
  }
  
  return (
    <Card className={`mb-8 border border-primary/20 bg-primary/5 shadow-sm ${isNavigating ? 'opacity-70 pointer-events-none' : ''} transition-all duration-300`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium flex items-center">
          <MapPin className="h-5 w-5 mr-2 text-primary" />
          <span className="text-gradient">Associated Itineraries</span>
        </CardTitle>
        <CardDescription>
          Explore the itineraries mentioned in this article
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3">
          {itineraries.map((itinerary) => (
            <div
              key={itinerary.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-md border border-border/60 bg-card/30 hover:bg-card/60 hover:border-primary/30 hover:shadow-md transition-all duration-200 group cursor-pointer animate-fade-in"
              onClick={() => navigate(`/itineraries/${itinerary.slug || itinerary.id}`)}
            >
              <div className="mb-3 sm:mb-0">
                <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">{itinerary.title}</h3>
                {itinerary.destination && (
                  <p className="text-sm text-muted-foreground mt-0.5 flex items-center">
                    <MapPin className="h-3 w-3 mr-1 inline-block" />
                    {itinerary.destination}
                  </p>
                )}
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="self-end sm:self-auto group-hover:bg-primary/10 transition-all duration-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/itineraries/${itinerary.slug || itinerary.id}`);
                      }}
                    >
                      <span className="mr-1 group-hover:translate-x-[-2px] transition-transform duration-200">View Details</span>
                      <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View full itinerary details</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
